import { useEffect, useState } from "react";
import {
  Phone,
  Handshake,
  Wallet,
  CheckCircle2,
  FolderKanban,
} from "lucide-react";
import { useSelector } from "react-redux";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import RecoveryTrendChart from "../../components/dashboard/charts/RecoveryTrendChart";
import AgingBucketsChart from "../../components/dashboard/charts/AgingBucketsChart";
import PortfolioMixChart from "../../components/dashboard/charts/PortfolioMixChart";
import ChannelPerformanceChart from "../../components/dashboard/charts/ChannelPerformanceChart";
import CollectionFunnelChart from "../../components/dashboard/charts/CollectionFunnelChart";
import WeeklyPulseChart from "../../components/dashboard/charts/WeeklyPulseChart";
import RegionalCollectionsChart from "../../components/dashboard/charts/RegionalCollectionsChart";
import PaymentMethodsChart from "../../components/dashboard/charts/PaymentMethodsChart";
import TopPortfoliosChart from "../../components/dashboard/charts/TopPortfoliosChart";
import { formatCurrency } from "../../data/mock/dashboardData";
import { USE_MOCK_AUTH } from "../../config/api";
import { useLiveEventTick } from "../../hooks/useLiveRefresh";
import {
  fetchProviderDashboardCharts,
  fetchProviderDashboardOverview,
  fetchProviderDashboardPriorityCases,
} from "../../services/dashboardApi";
import { showError } from "../../utils/toast";

const PERIODS = [
  { key: "7D", label: "7 days" },
  { key: "30D", label: "30 days" },
  { key: "90D", label: "90 days" },
  { key: "YTD", label: "Year to date" },
];

function Panel({ children, className = "" }) {
  return (
    <div
      className={`flex h-full flex-col rounded-xl border border-primary/[0.07] bg-white p-5 shadow-[0_1px_2px_rgba(127,29,29,0.04)] ${className}`}
    >
      {children}
    </div>
  );
}

function PanelHeader({ title, subtitle, action }) {
  return (
    <div className="mb-4 flex shrink-0 items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-bold text-primary">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-primary/40">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function formatTrendDate(day, withYear = false) {
  const iso = String(day || "").slice(0, 10);
  const date = new Date(`${iso}T12:00:00`);
  if (!iso || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GH", {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

function weekdayLabel(day) {
  try {
    return new Date(`${day}T12:00:00`).toLocaleDateString("en-GH", { weekday: "short" });
  } catch {
    return String(day || "").slice(5);
  }
}

export default function DashboardHome() {
  const { user, token } = useSelector((state) => state.auth);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [period, setPeriod] = useState("30D");
  const [overview, setOverview] = useState(null);
  const [charts, setCharts] = useState(null);
  const [priorityCases, setPriorityCases] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(useLiveApi);
  const [loadingCharts, setLoadingCharts] = useState(useLiveApi);
  const liveDashTick = useLiveEventTick(/^(portfolio\.|case\.|payment\.)/);

  useEffect(() => {
    if (!useLiveApi) {
      setLoadingOverview(false);
      return undefined;
    }
    let cancelled = false;
    const quiet = Boolean(liveDashTick);
    if (!quiet) setLoadingOverview(true);
    fetchProviderDashboardOverview(token, { force: quiet })
      .then((data) => {
        if (!cancelled) setOverview(data || null);
      })
      .catch((error) => {
        if (!cancelled) showError(error.message || "Could not load dashboard overview.");
      })
      .finally(() => {
        if (!cancelled) setLoadingOverview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi, liveDashTick]);

  useEffect(() => {
    if (!useLiveApi) {
      setLoadingCharts(false);
      return undefined;
    }
    let cancelled = false;
    const quiet = Boolean(liveDashTick);
    if (!quiet) setLoadingCharts(true);
    Promise.allSettled([
      fetchProviderDashboardCharts(token, period, { force: quiet }),
      fetchProviderDashboardPriorityCases(token, { force: quiet }),
    ]).then(([chartsResult, priorityResult]) => {
      if (cancelled) return;
      if (chartsResult.status === "fulfilled") {
        setCharts(chartsResult.value || null);
      } else {
        setCharts(null);
        showError(chartsResult.reason?.message || "Could not load dashboard charts.");
      }
      if (priorityResult.status === "fulfilled") {
        setPriorityCases(priorityResult.value || []);
      } else {
        setPriorityCases([]);
      }
      setLoadingCharts(false);
    });
    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi, period, liveDashTick]);

  const periodLabel = PERIODS.find((item) => item.key === period)?.label ?? "30 days";
  const chartsForPeriod = charts?.period === period ? charts : null;

  const paymentTrend = (() => {
    const rows = Array.isArray(chartsForPeriod?.payments_by_day)
      ? chartsForPeriod.payments_by_day
      : Array.isArray(chartsForPeriod?.payments_last_30_days)
        ? chartsForPeriod.payments_last_30_days
        : [];
    if (!rows.length) return null;
    return {
      labels: rows.map((row) => row.label || formatTrendDate(row.day) || String(row.day || "")),
      collected: rows.map((row) => Number(row.amount) || 0),
    };
  })();

  const trendRangeLabel = (() => {
    const start = chartsForPeriod?.range?.start || chartsForPeriod?.payments_by_day?.[0]?.day;
    const end =
      chartsForPeriod?.range?.end || chartsForPeriod?.payments_by_day?.at?.(-1)?.day;
    if (!start || !end) return periodLabel;
    return `${periodLabel} · ${formatTrendDate(start, true)} – ${formatTrendDate(end, true)}`;
  })();

  const statusStages = (() => {
    const byStatus = charts?.cases_by_status;
    if (!byStatus || typeof byStatus !== "object") return null;
    return Object.entries(byStatus).map(([label, count]) => ({
      label: !label || String(label).trim().toLowerCase() === "open" ? "Open" : String(label),
      count: Number(count) || 0,
    }));
  })();

  const portfolioItems = (() => {
    const rows = Array.isArray(charts?.cases_by_portfolio_group) ? charts.cases_by_portfolio_group : [];
    if (!rows.length) return null;
    return rows.map((r) => ({
      name: r.label || r.name || "Unknown",
      outstanding: Number(r.outstanding) || 0,
      total: Number(r.total) || 0,
    }));
  })();

  const regionItems = (() => {
    const rows = Array.isArray(charts?.collections_by_region) ? charts.collections_by_region : [];
    if (!rows.length) return null;
    return rows.map((r) => ({
      name: r.label || r.name || "Unknown",
      amount: Number(r.collected ?? r.outstanding ?? r.amount) || 0,
      accounts: Number(r.total ?? r.accounts) || 0,
    }));
  })();

  const weeklyDays = (Array.isArray(charts?.weekly_pulse) ? charts.weekly_pulse : []).map((row) => ({
    label: weekdayLabel(row.day),
    amount: Number(row.amount) || 0,
  }));

  const paymentMethods = (Array.isArray(charts?.payments_by_method) ? charts.payments_by_method : []).map(
    (row) => ({
      label: row.method || row.label || "Other",
      amount: Number(row.amount) || 0,
    })
  );

  const channels = (Array.isArray(charts?.channels) ? charts.channels : []).map((row) => ({
    name: row.channel || row.name || "Channel",
    total: Number(row.total) || 0,
  }));

  const metrics = [
    {
      label: "Debt settled",
      value: loadingOverview ? "…" : formatCurrency(overview?.debt_settled ?? overview?.total_paid ?? 0),
      sub: "Full, partial & instalment payments",
      icon: CheckCircle2,
      tone: "emerald",
    },
    {
      label: "Amount owed",
      value: loadingOverview ? "…" : formatCurrency(overview?.total_owed ?? 0),
      sub: `${(overview?.total_cases ?? 0).toLocaleString()} accounts`,
      icon: Wallet,
      tone: "primary",
    },
    {
      label: "Active cases",
      value: loadingOverview ? "…" : (overview?.active_cases ?? 0).toLocaleString(),
      sub: `${formatCurrency(overview?.outstanding ?? 0)} outstanding`,
      icon: FolderKanban,
      tone: "accent",
    },
  ];

  const insights = [
    {
      icon: FolderKanban,
      title: `${overview?.portfolio_groups ?? 0} portfolio groups`,
      detail: `${overview?.uploads ?? 0} uploads · ${overview?.customers ?? 0} customers`,
    },
    {
      icon: Handshake,
      title: `${formatCurrency(overview?.total_collected ?? overview?.total_paid ?? 0)} collected`,
      detail: `${overview?.payments_count ?? 0} payment records`,
    },
    {
      icon: Phone,
      title: `${overview?.contact_attempts_today ?? 0} contacts today`,
      detail: "Logged against your portfolio",
    },
  ];

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="mx-auto max-w-8xl px-4 pb-10 pt-5 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 border-b border-primary/[0.06] pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium text-primary/40">{user?.institution}</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-primary sm:text-[1.75rem]">
            {greeting},{" "}
            <span className="text-primary/80">{user?.name?.split(/\s+/)[0] ?? "there"}</span>
          </h1>
          <p className="mt-1.5 max-w-md text-sm text-primary/45">
            {loadingOverview
              ? "Loading summary…"
              : `${formatCurrency(overview?.outstanding ?? 0)} outstanding across ${(overview?.total_cases ?? 0).toLocaleString()} accounts`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-primary/10 bg-white p-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  period === p.key ? "bg-primary text-white" : "text-primary/45 hover:text-primary"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <StatCardGrid cols={3} className="mt-4">
        {metrics.map((m) => (
          <StatCard key={m.label} {...m} />
        ))}
      </StatCardGrid>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {insights.map((item) => (
          <div
            key={item.title}
            className="flex gap-3 rounded-xl border border-primary/[0.07] bg-gradient-to-br from-primary/[0.03] to-accent/[0.06] px-4 py-3.5"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
              <item.icon className="h-4 w-4 text-primary" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-bold text-primary">{item.title}</p>
              <p className="mt-0.5 text-[11px] text-primary/45">{item.detail}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-12">
        <Panel className="lg:col-span-8">
          <PanelHeader
            title="Collection trend"
            subtitle={
              loadingCharts || !chartsForPeriod
                ? "Loading payments…"
                : paymentTrend?.collected?.some((amount) => amount > 0)
                  ? trendRangeLabel
                  : `${trendRangeLabel} · no payments recorded`
            }
          />
          <RecoveryTrendChart
            key={period}
            labels={paymentTrend?.labels}
            collected={paymentTrend?.collected}
            subtitle={trendRangeLabel}
          />
        </Panel>

        <Panel className="lg:col-span-4">
          <PanelHeader title="Overdue balances" subtitle="Grouped by how long payments are late" />
          <AgingBucketsChart buckets={charts?.aging_buckets} />
        </Panel>
      </div>

      <div className="mt-4 grid items-stretch gap-4 lg:grid-cols-12">
        <Panel className="lg:col-span-7">
          <PanelHeader title="Cases by status" subtitle="Live case distribution" />
          <CollectionFunnelChart stages={statusStages || undefined} />
        </Panel>

        <Panel className="lg:col-span-5">
          <PanelHeader title="This week" subtitle="Daily collections" />
          <WeeklyPulseChart days={weeklyDays} />
        </Panel>
      </div>

      <div className="mt-4 grid items-stretch gap-4 lg:grid-cols-12">
        <Panel className="flex lg:col-span-6">
          <PanelHeader title="Collections by region" subtitle="Where money is coming from" />
          <RegionalCollectionsChart regions={regionItems || undefined} />
        </Panel>

        <Panel className="lg:col-span-3">
          <PanelHeader title="How customers pay" subtitle="Payment method split" />
          <PaymentMethodsChart methods={paymentMethods} />
        </Panel>

        <Panel className="lg:col-span-3">
          <PanelHeader title="Top portfolios" subtitle="Highest outstanding groups" />
          <TopPortfoliosChart items={portfolioItems || undefined} />
        </Panel>
      </div>

      <div className="mt-4 grid items-stretch gap-4 lg:grid-cols-12">
        <Panel className="lg:col-span-5">
          <PanelHeader
            title="Outreach channels"
            subtitle="Contact volume by channel — SMS, WhatsApp, email and phone"
          />
          <ChannelPerformanceChart channels={channels} />
        </Panel>

        <Panel className="min-h-0 overflow-hidden lg:col-span-4">
          <PanelHeader title="Portfolio mix" subtitle="By portfolio batch" />
          <PortfolioMixChart items={portfolioItems || undefined} />
        </Panel>

        <Panel className="lg:col-span-3">
          <PanelHeader
            title="Needs attention"
            subtitle="Highest overdue active cases"
            action={
              <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-accent/20 px-1.5 text-[10px] font-bold text-primary">
                {priorityCases.length}
              </span>
            }
          />
          {!priorityCases.length ? (
            <p className="py-8 text-center text-sm text-primary/40">
              {loadingCharts ? "Loading…" : "No priority cases right now."}
            </p>
          ) : (
            <ul className="space-y-2">
              {priorityCases.map((c) => (
                <li
                  key={c.id || c.case_id}
                  className="flex items-center gap-2.5 rounded-lg border border-primary/[0.05] bg-primary/[0.015] px-2.5 py-2 transition-colors hover:border-primary/10 hover:bg-primary/[0.03]"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/[0.08] text-primary/50">
                    <Phone className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-primary">{c.name}</p>
                    <p className="text-[10px] text-primary/40">
                      {c.id || c.case_id} · {c.channel || "Follow-up"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-primary">{formatCurrency(c.amount || 0)}</p>
                    <p className="text-[10px] text-primary/35">{c.days_overdue ?? 0}d late</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
